import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Input, Row } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";

// Import Chart.js and register modules
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler   // ✅ Add this
} from "chart.js";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { getSalesGraphAPI } from "./service/dashboard-service";
import { H6 } from "../../AbstractElements";
import DropdownCommon from "../Common/Dropdown";
import { Filter, X } from "react-feather";
import { toast } from "react-toastify";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler    // ✅ Register filler
);

// const fetchSalesData = () =>
// 	new Promise((resolve) => {
// 		setTimeout(() => {
// 			resolve([
// 				{ date: "28/08/2025", sales: 80 },
// 				{ date: "30/08/2025", sales: 150 },
// 				{ date: "31/08/2025", sales: 220 },
// 				{ date: "01/09/2025", sales: 120 },
// 				{ date: "02/09/2025", sales: 200 },
// 				{ date: "03/09/2025", sales: 150 },
// 				{ date: "04/09/2025", sales: 300 },
// 			]);
// 		}, 1000);
// 	});

const SalesLineChart = () => {
	const { t } = useTranslation();
	const [data, setData] = useState([]);
	const { loading, setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();
	const [duration, setDuration] = useState("weekly"); // default value
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");

	const primary = "#1b255b";
	const WeeklyMonDropdown = ["Weekly", "Monthly", "Yearly", "Custom"];

	useEffect(() => {
		getSales();
		// fetchSalesData().then((res) => {
		// 	setData(res);
		// 	setLoading(false);
		// });
	}, []);

	// const getSales = async (selectedDuration = duration, customFrom = "", customTo = "") => {
	// 	const body = {
	// 		clinicId: pharmacyPOS?.hospital_id,
	// 		pharmacyId: pharmacyPOS?._id,
	// 		duration: selectedDuration,
	// 		startDate: customFrom,
	// 		endDate: customTo
	// 	};

	// 	try {
	// 		setLoading(true);
	// 		const response = await getSalesGraphAPI(body);

	// 		if (response.data.status === "success" && response.data.data) {
	// 			const { paymentAmounts, dates } = response.data.data;

	// 			// ✅ Safely map arrays
	// 			const formatted = dates.map((d, i) => ({
	// 				// format as "11 Sep" instead of DD/MM/YYYY
	// 				date: new Date(d).toLocaleDateString("en-US", {
	// 					day: "2-digit",
	// 					month: "short"
	// 				}),
	// 				sales: paymentAmounts[i] ?? 0
	// 			}));

	// 			// console.log("Formatted Graph Data:", formatted);
	// 			setData(formatted);
	// 		}
	// 	} catch (error) {
	// 		console.error("Error fetching response", error);
	// 	} finally {
	// 		setLoading(false);
	// 	}
	// };

	const getSales = async (selectedDuration = duration, customFrom = "", customTo = "") => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			pharmacyId: pharmacyPOS?._id,
			duration: selectedDuration,
			startDate: customFrom,
			endDate: customTo
		};

		try {
			setLoading(true);
			const response = await getSalesGraphAPI(body);

			if (response.data.status === "success" && response.data.data) {
				const { paymentAmounts, dates } = response.data.data;

				const today = new Date();

				// Transform graph data and exclude any future dates
				const formatted = dates.reduce((acc, date, index) => {
					const currentDate = new Date(date);

					// Skip future dates so graph only shows up to today
					if (currentDate > today) {
						return acc;
					}

					const payments = paymentAmounts[index] || {};

					acc.push({
						date: currentDate.toLocaleDateString("en-US", {
							day: "2-digit",
							month: "short"
						}),
						sales: payments
					});

					return acc;
				}, []);

				setData(formatted);
			}
		} catch (error) {
			console.error("Error fetching sales graph:", error);
		} finally {
			setLoading(false);
		}
	};

	// Extract all unique currencies from API data
	const currencies = Array.from(
		new Set(
			data.flatMap(d => Object.keys(d.sales || {}))
		)
	);

	// Build datasets for each currency
	const datasets = currencies.map((currency, index) => {
		const colors = ["#1abc9c", "#3498db", "#9b59b6", "#e67e22", "#e74c3c"];
		const color = colors[index % colors.length];

		return {
			label: `${t("Sales in")} ${currency}`,
			data: data.map(d => d.sales[currency] ?? 0),
			borderColor: color,
			backgroundColor: color + "33",
			pointBackgroundColor: color,
			tension: 0.4,
			fill: false
		};
	});

	const chartData = {
		labels: data.map(d => d.date),
		datasets
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: true,
				position: "top",
			},
			tooltip: {
				mode: "index",
				intersect: false,
			},
		},
		scales: {
			x: {
				type: "category",
				title: {
					display: true,
					text: t("Date"),
				},
			},
			y: {
				beginAtZero: true,
				title: {
					display: true,
					text: t("Sales"),
				},
			},
		},
	};

	// Apply custom filter
	const handleApplyFilter = () => {
		if (fromDate && toDate) {
			if (new Date(toDate) < new Date(fromDate)) {
				toast.warning(t("⚠️ 'To Date' must be after 'From Date'"));
				return;
			}
			setDuration("custom");
			getSales("custom", fromDate, toDate);
		}
	};

	// Reset to dropdown mode
	const handleResetFilter = () => {
		setFromDate("");
		setToDate("");
		setDuration("weekly");
		getSales("weekly");
	};

	return (
		<Card>
			<CardHeader>
				<div className='header-top'>
					<H6>{t("Sales")}</H6>

					{/* Show date filters only if custom */}
					{duration === "custom" && (
						<Row className="align-items-center g-2">
							<Col>
								<div className="form-group d-flex align-items-center mb-0">
									<label className="form-label small mb-0 me-1">{t("From")}</label>
									<Input
										type="date"
										className="form-control-sm"
										value={fromDate}
										onChange={(e) => setFromDate(e.target.value)}
										placeholder="From (dd-mm-yyyy)"
										style={{ fontSize: "12.25px" }}
									/>
								</div>
							</Col>
							<Col>
								<div className="form-group d-flex align-items-center mb-0">
									<label className="form-label small mb-0 me-1">{t("To")}</label>
									<Input
										type="date"
										className="form-control-sm"
										value={toDate}
										min={fromDate}
										max={new Date().toISOString().split("T")[0]}
										onChange={(e) => setToDate(e.target.value)}
										style={{ fontSize: "12.25px" }}
									/>
								</div>
							</Col>
							<Col>
								<Button color="primary" size="sm" onClick={handleApplyFilter}>
									<i className="fa fa-filter"></i>
								</Button>
							</Col>
							<Col>
								<Button color="secondary" size="sm" onClick={handleResetFilter}>
									<i className="fa fa-times"></i>
								</Button>
							</Col>
						</Row>
						// <Row className="g-2">
						// 	<Col>
						// 		<div className="form-floating">
						// 			<Input
						// 				id="fromDate"
						// 				type="date"
						// 				className="form-control form-control-sm"
						// 				value={fromDate}
						// 				onChange={(e) => setFromDate(e.target.value)}
						// 			/>
						// 			<label htmlFor="fromDate">{t("From Date")}</label>
						// 		</div>
						// 	</Col>
						// 	<Col>
						// 		<div className="form-floating">
						// 			<Input
						// 				id="toDate"
						// 				type="date"
						// 				className="form-control form-control-sm"
						// 				value={toDate}
						// 				min={fromDate}
						// 				onChange={(e) => setToDate(e.target.value)}
						// 			/>
						// 			<label htmlFor="toDate">{t("To Date")}</label>
						// 		</div>
						// 	</Col>
						// 	<Col xs="auto">
						// 		<Button color="success" size="sm" onClick={handleApplyFilter}>
						// 			<i className="fa fa-filter"></i>
						// 		</Button>
						// 	</Col>
						// 	<Col xs="auto">
						// 		<Button color="warning" size="sm" onClick={handleResetFilter}>
						// 			<i className="fa fa-times"></i>
						// 		</Button>
						// 	</Col>
						// </Row>
					)}

					<DropdownCommon
						dropdownMain={{ className: "icon-dropdown", direction: "start" }}
						options={WeeklyMonDropdown}
						iconName="icon-more-alt"
						btn={{ tag: "span" }}
						onSelect={(value) => {
							setDuration(value.toLowerCase()); // store "weekly", "monthly", "yearly"
							if (value.toLowerCase() !== "custom") {
								getSales(value.toLowerCase());
							}
							// getSales(value.toLowerCase());   // refetch when changed
						}}
					/>
				</div>
			</CardHeader>
			<CardBody>
				{loading ? (
					<Skeleton height={200} />
				) : (
					<Line data={chartData} options={chartOptions} height={400} />
				)}
			</CardBody>
		</Card>
	);
};

export default SalesLineChart;
